import { useEffect, useRef } from 'react';
import HugeRTE, { Editor, EditorEvent } from 'hugerte';
import { ErnotsConfig } from '../../../ErnotsConfig';

export function HugeRTEComponent(props: Props)
{
    const editorRef = useRef<Editor|null>(null);
    const {id, value, onChange, onBlur, onFocus} = props;


    useEffect(function HugeRTEEffect()
    {
        if (editorRef.current)
        {
            return;
        }

        //Initialize HugeRTE
        HugeRTE.init({
            selector: `#${id}`,
            base_url: ErnotsConfig.BaseURL + 'hugerte/',
            plugins: 'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table code help wordcount',
            toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat',
            mobile:
            {
                /*
                    Gives mobile back the menubar, which is otherwise hidden.

                    Removes the "help" menu, because all it helps with is keyboard shortcuts, which are useless on phones.
                    And it makes the menu overflow.
                */
                menubar: 'file edit insert view format table tools'
            },

            setup: function HugeRTESetup(editor: Editor)
            {
                //Save the editor instance in the ref
                editorRef.current = editor;

                let lastContents = '';
                function dispatchChange(e: EditorEvent<any>)
                {
                    const newContents = editor.getContent()
                    if (onChange && newContents !== lastContents)
                    {
                        onChange(editor.getContent());
                        lastContents = newContents;
                    }
                }

                //Trigger onChange when content changes
                //Also whenever the editor uses one of its custom ExecCommand events, of which there are many.
                //In particular, 'undo' and 'redo' send them, but not 'change'.
                editor.on('change', dispatchChange);
                editor.on('ExecCommand', dispatchChange);

                editor.on('focus', function()
                {
                    onFocus?.();
                });

                editor.on('blur', function()
                {
                    onBlur?.();
                });
            },


        });

        //Cleanup on component unmount
        return function()
        {
            editorRef.current?.destroy();
            editorRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    useEffect(function()
    {
        /**
         * The editor only changes its contens due to its internal events.
         * With this, I force it to change them according to the prop.
         * Also, add a save point to the undo manager.
         */
        if (value !== editorRef.current?.getContent())
        {
            editorRef.current?.undoManager?.transact(() => editorRef.current?.setContent(value));            
        }
    }, [value]);

    return (
        <textarea
            id={id}
            value={value}
            readOnly
        />
    );
};

interface Props {
    id: string;
    value: string;
    onChange?: (content: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
}
